import React from "react";
import { ShoppingCart, CreditCard, Heart } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerContext";

const priceLabel = (n) => "$ " + (Math.round(Number(n) || 0)).toLocaleString("es-CO");

export default function BookCard({ book, index = 0, onOpen, onBuy }) {
  const { addItem } = useCart();
  const { customer, isFavorite, toggleFavorite } = useCustomer();
  const fav = isFavorite(book.id);
  const outOfStock = typeof book.stock === "number" && book.stock <= 0;

  const handleAdd = (e) => {
    e.stopPropagation();
    addItem(book);
    toast.success(`"${book.title}" agregado al carrito`);
  };

  const handleBuy = (e) => {
    e.stopPropagation();
    onBuy?.(book);
  };

  const handleFav = (e) => {
    e.stopPropagation();
    toggleFavorite(book);
  };

  return (
    <div
      onClick={() => onOpen?.(book)}
      className="lc-fade-up group flex flex-row bg-white rounded-2xl p-4 gap-4 border border-[var(--lc-border)] shadow-[0_2px_10px_rgba(27,42,91,0.05)] hover:shadow-[0_14px_30px_-8px_rgba(27,42,91,0.18)] hover:-translate-y-1 transition-[transform,box-shadow] duration-300 cursor-pointer"
      style={{ animationDelay: `${Math.min(index, 8) * 55}ms` }}
      data-testid={`book-card-${book.id}`}
    >
      <div className="rounded-xl overflow-hidden bg-[var(--lc-cream)] shrink-0 self-start relative">
        <img
          src={book.image_url}
          alt={`Portada de ${book.title}`}
          loading="lazy"
          className="block w-28 sm:w-32 h-40 sm:h-44 object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <button
          onClick={handleFav}
          data-testid={`favorite-toggle-${book.id}`}
          aria-label={fav ? "Quitar de favoritos" : "Agregar a favoritos"}
          className={`absolute top-2 right-2 w-8 h-8 grid place-items-center rounded-full bg-white/90 backdrop-blur shadow hover:scale-110 transition-transform ${customer ? "" : "hidden"}`}
        >
          <Heart className={`w-4 h-4 ${fav ? "fill-[var(--lc-primary)] text-[var(--lc-primary)]" : "text-[var(--lc-slate)]"}`} />
        </button>
        {outOfStock && (
          <span
            data-testid={`agotado-badge-${book.id}`}
            className="absolute top-2 left-2 rounded-full bg-[var(--lc-accent)] text-white text-[10px] font-bold px-2 py-0.5 shadow"
          >
            Agotado
          </span>
        )}
      </div>

      <div className="flex flex-col flex-1 min-w-0">
        <h3 className="font-display font-semibold text-[16px] text-[var(--lc-ink)] leading-tight line-clamp-2">
          {book.title}
        </h3>
        <p className="text-sm text-[var(--lc-slate)] mt-0.5 line-clamp-1">{book.author}</p>
        {book.publisher && (
          <p className="text-xs text-[var(--lc-slate)]/80 mt-0.5 line-clamp-1" data-testid={`book-publisher-${book.id}`}>
            Editorial: {book.publisher}
          </p>
        )}

        <p className="text-sm text-[var(--lc-slate)] mt-2 line-clamp-3 leading-relaxed">
          {book.synopsis}
        </p>

        <div className="mt-auto pt-3">
          <span className="font-display font-bold text-lg text-[var(--lc-ink)]" data-testid={`book-price-${book.id}`}>
            {priceLabel(book.price)}
          </span>
          <div className="mt-2 flex gap-1.5">
            {outOfStock ? (
              <button
                onClick={handleBuy}
                data-testid={`preorder-${book.id}`}
                className="flex-1 flex items-center justify-center gap-1 rounded-full bg-[var(--lc-accent)] hover:opacity-90 text-white px-2 py-1.5 text-xs font-semibold transition-opacity shadow-[0_6px_14px_-4px_rgba(225,70,90,0.5)]"
              >
                <CreditCard className="w-3.5 h-3.5" />
                Encargar
              </button>
            ) : (
              <>
                <button
                  onClick={handleAdd}
                  data-testid={`add-to-cart-${book.id}`}
                  className="flex-1 flex items-center justify-center gap-1 rounded-full border-2 border-[var(--lc-primary)] text-[var(--lc-primary)] bg-white hover:bg-[var(--lc-primary)]/5 px-2 py-1.5 text-xs font-semibold transition-colors"
                >
                  <ShoppingCart className="w-3.5 h-3.5" />
                  Agregar
                </button>
                <button
                  onClick={handleBuy}
                  data-testid={`buy-now-${book.id}`}
                  className="flex-1 flex items-center justify-center gap-1 rounded-full bg-[var(--lc-primary)] hover:bg-[var(--lc-primary-hover)] text-white px-2 py-1.5 text-xs font-semibold transition-colors shadow-[0_6px_14px_-4px_rgba(225,70,90,0.5)]"
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  Comprar
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
